import { isEmpty } from 'bold-ui'
import {
  AtestadoEmBrancoPlanoInput,
  AtestadoPadraoPlanoInput,
  AtestadoPlanoInput,
  CiapPlanoInput,
  EvolucaoDenteInput,
  EvolucaoOutroInput,
  LicencaMaternidadePlanoInput,
  MedicamentoPlanoInput,
  OdontologiaInput,
  ParteBucalEnum,
  PlanoInput,
  PrescricaoMedicamentoPlanoInput,
  ProcedimentosPlanoInput,
  SituacaoFaceEnum,
  SituacaoRaizEnum,
  TecidoMoleInput,
  TipoAtestadoEnum,
} from 'graphql/types.generated'
import { concat } from 'lodash'

import { AtestadoPlanoModel } from './atestados/AtestadosView'
import { ArcadaFieldModel } from './evolucoes-odontologicas/components/odontograma-field/arcadas-field/ArcadasField'
import { DenteFieldModel } from './evolucoes-odontologicas/components/odontograma-field/dentes-field/DentesField'
import { EvolucaoDenteSupranumerarioModel } from './evolucoes-odontologicas/components/odontograma-field/dentes-supranumerarios/DentesSupranumerariosField'
import { OdontogramaFieldModel } from './evolucoes-odontologicas/components/odontograma-field/OdontogramaField'
import { OutrosFieldModel } from './evolucoes-odontologicas/components/odontograma-field/outros-field/OutrosField'
import { SextanteFieldModel } from './evolucoes-odontologicas/components/odontograma-field/sextantes-field/SextantesField'
import { getDentePosicaoProtese, isCoroaDisabled } from './evolucoes-odontologicas/components/odontograma-field/util'
import { OdontologiaModel } from './evolucoes-odontologicas/EvolucoesOdontologicasView'
import { TecidosMolesFieldModel } from './evolucoes-odontologicas/tecidos-moles/TecidosMolesField'
import { PlanoFormModel } from './PlanoForm'
import { PrescricaoMedicamentoPlanoModel } from './prescricao-medicamento/PrescricaoMedicamentoView'

export const convertPlanoFormToInput = (form: PlanoFormModel): PlanoInput =>
  form && {
    texto: form.texto,
    ciaps: form.ciaps?.map((ciap) => ({ id: ciap.ciap.id, observacao: ciap.observacao } as CiapPlanoInput)),
    procedimentos: form.procedimentos?.map(
      (procedimento) =>
        ({ id: procedimento.procedimento.id, observacao: procedimento.observacao } as ProcedimentosPlanoInput)
    ),
    prescricaoMedicamento:
      form?.prescricaoMedicamento && !isEmpty(form.prescricaoMedicamento.medicamentos)
        ? convertPrescricaoPlanoToInput(form.prescricaoMedicamento)
        : null,
    odontologia: form.odontologia && convertOdontologiaPlanoToInput(form.odontologia),
    atestado: form.atestado && convertAtestadoPlanoToInput(form.atestado),
  }

const convertPrescricaoPlanoToInput = (
  prescricaoMedicamento: PrescricaoMedicamentoPlanoModel
): PrescricaoMedicamentoPlanoInput => {
  return {
    medicamentos: prescricaoMedicamento.medicamentos.map(
      (medicamento) =>
        ({
          registroManual: !!medicamento.registroManual,
          medicamento: medicamento.principioAtivoCombo?.medicamento?.id,
          medicamentoText: medicamento.principioAtivoText,
          concentracao: medicamento.concentracaoText,
          viaAdministracao: medicamento.viaAdministracao?.id,
          tipoReceita: !!medicamento.registroManual
            ? medicamento.tipoReceita.tipoReceitaEnum
            : medicamento.principioAtivoCombo.principioAtivo.listaMaterial.tipoReceita,
          formaFarmaceutica: !!medicamento.registroManual
            ? medicamento.formaFarmaceutica?.id
            : medicamento.principioAtivoCombo.medicamento.formaFarmaceutica.id,

          dose: medicamento.dose,
          doseUnica: !!medicamento.doseUnica,
          usoContinuo: !!medicamento.usoContinuo,
          tipoFrequencia: !medicamento.doseUnica ? medicamento.tipoFrequencia : null,
          intervaloDose: medicamento.intervaloDose,
          frequenciaDose: medicamento.frequenciaDose,
          // valores para aba de frequencia e turno
          quantidadePeriodoFrequenciaTurno: medicamento.quantidadePeriodoFrequenciaTurno,
          unidadeMedidaTempoFrequenciaTurno: medicamento.unidadeMedidaTempoFrequenciaTurno,

          turno: medicamento.turno,
          posologia: medicamento.posologia,
          dataInicioTratamento: medicamento.dataInicioTratamento,
          dataFimTratamento: medicamento.dataFimTratamento,
          duracao: medicamento.duracao,
          escalaDuracao: medicamento.escalaDuracao,
          quantidade: medicamento.quantidade,
          recomendacoes: medicamento.recomendacoes,
          unidadeFornecimento: !!medicamento.registroManual
            ? medicamento.unidadeFornecimento
            : medicamento.principioAtivoCombo.unidadeFornecimento.nome,
        } as MedicamentoPlanoInput)
    ),
  } as PrescricaoMedicamentoPlanoInput
}

const convertOdontologiaPlanoToInput = (odontologia: OdontologiaModel): OdontologiaInput => ({
  ...convertOdontogramaPlanoToInput(odontologia.odontograma),
  tecidosMoles: odontologia?.tecidosMoles && convertTecidosMolesToInput(odontologia.tecidosMoles),
})

const convertTecidosMolesToInput = (tecidosMoles: TecidosMolesFieldModel[]): TecidoMoleInput[] =>
  tecidosMoles.map(
    (tecidoMole) =>
      ({
        local: tecidoMole.local.value,
        cid10: tecidoMole.problema?.id,
        procedimentos: tecidoMole.procedimentos?.map(({ id }) => id),
        observacao: tecidoMole.observacao,
        caracteristicasLesao:
          tecidoMole.caracteristicasLesao &&
          [
            tecidoMole.caracteristicasLesao.base,
            tecidoMole.caracteristicasLesao.consistencia,
            tecidoMole.caracteristicasLesao.cor,
            tecidoMole.caracteristicasLesao.lesaoFundamental?.lesaoEnvolvida,
            tecidoMole.caracteristicasLesao.quantidade,
            tecidoMole.caracteristicasLesao.sintamotologia?.value !== 'outros' &&
              tecidoMole.caracteristicasLesao.sintamotologia?.value,
            tecidoMole.caracteristicasLesao.superficie?.value,
            tecidoMole.caracteristicasLesao.tamanho,
          ].filter((item) => item),
        descricaoSintomatologia: tecidoMole.caracteristicasLesao?.sintamotologia?.descricaoOutro,
      } as TecidoMoleInput)
  )

const convertOdontogramaPlanoToInput = (odontograma: OdontogramaFieldModel): OdontologiaInput =>
  odontograma &&
  ({
    evolucoesDente:
      odontograma.dentes &&
      convertOdontogramaDentesToInput(
        odontograma.dentes,
        odontograma.proteseTotalSuperior,
        odontograma.proteseTotalInferior
      ),
    evolucoesOutro: concat(
      odontograma.sextantes && convertOdontogramaSextantesToInput(odontograma.sextantes),
      odontograma.arcadas && convertOdontogramaArcadasToInput(odontograma.arcadas),
      odontograma.outros && convertOdontogramaOutrosToInput(odontograma.outros),
      odontograma.dentesSupranumerarios &&
        convertOdontogramaDentesSupranumerariosToInput(odontograma.dentesSupranumerarios)
    ).filter((item) => !!item),
    aparelho: odontograma.possuiAparelho,
    contencao: odontograma.possuiContencao,
    proteseTotalInferior: odontograma.proteseTotalInferior,
    proteseTotalSuperior: odontograma.proteseTotalSuperior,
  } as OdontologiaInput)

const convertOdontogramaSextantesToInput = (
  sextantes: Record<ParteBucalEnum, SextanteFieldModel>
): EvolucaoOutroInput[] =>
  Object.entries(sextantes).map(
    ([key, sextante]) =>
      sextante.procedimentos?.length > 0 &&
      ({
        parteBucal: key,
        procedimentos: sextante.procedimentos.map(({ id }) => id),
        observacao: sextante.observacoes && sextante.observacoes[0],
      } as EvolucaoOutroInput)
  )

const convertOdontogramaArcadasToInput = (arcadas: Record<ParteBucalEnum, ArcadaFieldModel>): EvolucaoOutroInput[] =>
  Object.entries(arcadas).map(
    ([key, arcada]) =>
      arcada.procedimentos?.length > 0 &&
      ({
        parteBucal: key,
        procedimentos: arcada.procedimentos.map(({ id }) => id),
        observacao: arcada.observacoes && arcada.observacoes[0],
      } as EvolucaoOutroInput)
  )

const convertOdontogramaDentesToInput = (
  dentes: Record<ParteBucalEnum, DenteFieldModel>,
  proteseTotalSuperior?: boolean,
  proteseTotalInferior?: boolean
): EvolucaoDenteInput[] =>
  Object.entries(dentes).map(([key, dente]: [ParteBucalEnum, DenteFieldModel]) => {
    const coroaDisabled =
      isCoroaDisabled(dente) ||
      (getDentePosicaoProtese(key) === 'superior' ? proteseTotalSuperior : proteseTotalInferior)

    return {
      dente: key,
      situacoesRaiz:
        dente.condicoesDente &&
        dente.condicoesDente.filter((condicao) =>
          Object.values(SituacaoRaizEnum).includes(condicao as SituacaoRaizEnum)
        ),
      situacoesFace:
        dente.condicoesDente &&
        dente.condicoesDente.filter((condicao) =>
          Object.values(SituacaoFaceEnum).includes(condicao as SituacaoFaceEnum)
        ),
      procedimentos: dente.procedimentos?.map(({ id }) => id),
      observacao: dente.observacoes && dente.observacoes[0],
      situacaoCoroaCima:
        !coroaDisabled && dente.coroa?.faceTop?.activeColor ? dente.coroa.faceTop.activeColor.value : undefined,
      situacaoCoroaEsquerda:
        !coroaDisabled && dente.coroa?.faceLeft?.activeColor ? dente.coroa.faceLeft.activeColor.value : undefined,
      situacaoCoroaDireita:
        !coroaDisabled && dente.coroa?.faceRight?.activeColor ? dente.coroa.faceRight.activeColor.value : undefined,
      situacaoCoroaBaixo:
        !coroaDisabled && dente.coroa?.faceBottom?.activeColor ? dente.coroa.faceBottom.activeColor.value : undefined,
      situacaoCoroaCentro:
        !coroaDisabled && dente.coroa?.faceCenter?.activeColor ? dente.coroa.faceCenter.activeColor.value : undefined,
    } as EvolucaoDenteInput
  })

const convertOdontogramaOutrosToInput = (outros: OutrosFieldModel[]): EvolucaoOutroInput[] =>
  outros.map(
    (outro) =>
      outro.procedimentos?.length > 0 &&
      ({
        descricaoOutroLocal: outro.local,
        procedimentos: outro.procedimentos.map(({ id }) => id),
        observacao: outro.observacao,
      } as EvolucaoOutroInput)
  )

const convertOdontogramaDentesSupranumerariosToInput = (
  dentesSupranumerarios: EvolucaoDenteSupranumerarioModel[]
): EvolucaoOutroInput[] =>
  dentesSupranumerarios.map(
    (denteSupranumerario) =>
      denteSupranumerario.procedimentos?.length > 0 &&
      ({
        parteBucal: ParteBucalEnum.DENTE_SUPRANUMERARIO,
        descricaoOutroLocal: denteSupranumerario.local,
        procedimentos: denteSupranumerario.procedimentos.map(({ id }) => id),
        observacao: denteSupranumerario.observacao,
      } as EvolucaoOutroInput)
  )

const convertAtestadoPlanoToInput = (atestados: AtestadoPlanoModel): AtestadoPlanoInput => {
  const emBranco = atestados.atestadosRecentes?.filter((it) => it.tipo === TipoAtestadoEnum.EM_BRANCO)
  const padrao = atestados.atestadosRecentes?.filter((it) => it.tipo === TipoAtestadoEnum.PADRAO)
  const licencaMaternidade = atestados.atestadosRecentes?.filter(
    (it) => it.tipo === TipoAtestadoEnum.LICENCA_MATERNIDADE
  )
  return {
    emBranco: emBranco?.map((it) => ({ descricao: it.descricao } as AtestadoEmBrancoPlanoInput)),
    licencaMaternidade: licencaMaternidade?.map(
      (it) =>
        ({
          descricao: it.descricao,
          cpfCns: it.cpfCns,
          naoPossui: it.naoPossui,
        } as LicencaMaternidadePlanoInput)
    ),
    padrao: padrao?.map(
      (it) =>
        ({
          descricao: it.descricao,
          cpfCns: it.cpfCns,
          naoPossui: it.naoPossui,
          date: it.data,
          hora: it.hora,
          cid10: it.cid10?.id,
          dias: it.dias,
        } as AtestadoPadraoPlanoInput)
    ),
  } as AtestadoPlanoInput
}
